import React, { useEffect, useState } from "react"
import { Link, useStaticQuery, graphql } from "gatsby"
import { AnchorLink } from "gatsby-plugin-anchor-links"
import OutboundLink from "../components/OutboundLink"
import BackgroundSlider from "gatsby-image-background-slider"
import BackgroundImage from "gatsby-background-image"
import Layout from "../components/Layout"
import CloudCodePanel from "../components/CloudCodePanel"
import { GatsbyImage } from "gatsby-plugin-image"
import ExplainerPopup from "../components/ExplainerPopup"

import aws from "../images/homepage/aws.svg"
import azure from "../images/homepage/azure.svg"
import google from "../images/homepage/google.svg"
import connectivity from "../images/homepage/connectivity.svg"

import "./index.scss"
import ExternalLinkIcon from "../components/ExternalLinkIcon"

const FadeIn = ({ children }) => (
  <div
    data-sal="fade-in"
    data-sal-delay="0"
    data-sal-duration="150"
    data-sal-easing="ease-in-out"
  >
    {children}
  </div>
)

const partnerTiles = {
  AWS: (
    <OutboundLink
      key="aws"
      id="aws-tile"
      className="partner-tiles"
      href="https://aws.amazon.com/braket/"
      target="_blank"
      rel="noopener noreferrer nofollow"
    >
      <img className="aws-logo" id="aws-logo" alt="AWS logo" src={aws} />
      <span className="partner-link" id="aws-link">
        <span id="aws-text">Go to Amazon Braket </span>
        <ExternalLinkIcon />
      </span>
    </OutboundLink>
  ),
  Azure: (
    <OutboundLink
      key="azure"
      id="azure-tile"
      className="partner-tiles"
      href="https://azure.microsoft.com/en-us/services/quantum/"
      target="_blank"
      rel="noopener noreferrer nofollow"
    >
      <img
        className="azure-logo"
        id="azure-logo"
        alt="Microsoft Azure logo"
        src={azure}
      />
      <span className="partner-link" id="azure-link">
        <span id="azure-text">Go to Azure Quantum </span>
        <ExternalLinkIcon />
      </span>
    </OutboundLink>
  ),
  Google: (
    <OutboundLink
      key="google"
      id="google-tile"
      className="partner-tiles"
      href="https://console.cloud.google.com/marketplace/product/ionq-public/ionq"
      target="_blank"
      rel="noopener noreferrer nofollow"
    >
      <img
        className="google-logo"
        alt="Google Cloud logo"
        id="google-logo"
        src={google}
      />
      <span className="partner-link" id="google-link">
        <span id="google-text">Go to Google Cloud </span>
        <ExternalLinkIcon />
      </span>
    </OutboundLink>
  ),
}

const CloudPartners = ({ order }) => (
  <div className="partner-logos-container">
    {order.map(name => (
      <React.Fragment key={name}>{partnerTiles[name]}</React.Fragment>
    ))}
  </div>
)

const IndexPage = props => {
  const homepageData = useStaticQuery(graphql`
    {
      backgrounds: allFile(
        filter: { sourceInstanceName: { eq: "heroImages" } }
      ) {
        nodes {
          relativePath
          childImageSharp {
            gatsbyImageData(
              quality: 100
              width: 1577
              layout: CONSTRAINED
              placeholder: NONE
              backgroundColor: "transparent"
            )
          }
        }
      }
      allContentfulPressRelease(filter:{excludeFromHomepage:{ in:[null, false]}},sort:{date: DESC}) {
        nodes {
          slug
          title
        }
      }
      allContentfulQuantumSystem(filter:{includeInMeetOurModule:{ eq:true}}, sort:{navigationPosition:ASC}) {
        nodes {
          slug
          name
          shortBlurb
        }
      }
      allContentfulCustomer(sort:{position: ASC}) {
        nodes {
          name
          logo {
            url
          }
          url
        }
      }
      tempo: file(relativePath: { eq: "homepage/tempo.jpg" }) {
        childImageSharp {
          gatsbyImageData(quality: 100, layout: FULL_WIDTH, placeholder: NONE)
        }
      }
      box: file(relativePath: { eq: "homepage/box.png" }) {
        childImageSharp {
          gatsbyImageData(quality: 100, layout: FULL_WIDTH, placeholder: NONE)
        }
      }
      trap: file(relativePath: { eq: "homepage/trap.png" }) {
        childImageSharp {
          gatsbyImageData(quality: 100, layout: FULL_WIDTH, placeholder: NONE)
        }
      }
      companyBgDots: file(
        relativePath: { eq: "homepage/company-bg-dots.png" }
      ) {
        childImageSharp {
          gatsbyImageData(quality: 100, layout: FULL_WIDTH)
        }
      }
    }
  `)

  const [partnerOrder, updatepartnerOrder] = useState([])
  useEffect(() => {
    const cloudPartnersList = ["Google", "Azure", "AWS"]
    const minutes = new Date().getHours()
    const newPartnerOrder = []
    newPartnerOrder.push(cloudPartnersList.splice(minutes % 3, 1)[0])
    newPartnerOrder.push(cloudPartnersList.splice(minutes % 2, 1)[0])
    newPartnerOrder.push(cloudPartnersList.splice(0, 1)[0])
    updatepartnerOrder(newPartnerOrder)
  }, [])

  return (
    <Layout
      className="Homepage"
      id="homepage"
      title="IonQ | Trapped Ion Quantum Computing"
      location={props.location}
    >
      <div className="hero">
        <div className="hero-content">
          <div
            data-sal="fade-in"
            data-sal-delay="0"
            data-sal-duration="200"
            data-sal-easing="ease-in-out"
          >
            <Link
              to={`/news/${homepageData.allContentfulPressRelease.nodes[0].slug}`}
              className="small-news-group"
            >
              <span className="news-tag">News</span>
              {homepageData.allContentfulPressRelease.nodes[0].shortTitle ||
                homepageData.allContentfulPressRelease.nodes[0].title}
            </Link>
          </div>
          <div className="hero-headline-group">
            <h1
              className="hero-headline"
              data-sal="fade-in"
              data-sal-duration="200"
              data-sal-easing="ease-in-out"
            >
              Quantum is Now
              <span className="color-dot" aria-hidden>
                .
              </span>
            </h1>
            <p
              className="hero-copy"
              data-sal="fade-in"
              data-sal-delay="0"
              data-sal-duration="200"
              data-sal-easing="ease-in-out"
            >
              Quantum computing has the potential to change the world, and IonQ
              is leading the way.
            </p>
            <div
              className="hero-actions-group"
              data-sal="fade-in"
              data-sal-delay="0"
              data-sal-duration="200"
              data-sal-easing="ease-in-out"
            >
              <Link to="/get-started" className="hero-cta-button">
                Start your quantum journey
              </Link>
              <Link
                to="/get-started#leave-your-info"
                className="hero-secondary-button"
                state={{ prevPath: '/' }}
              >
                Contact a quantum solution specialist
              </Link>
            </div>
          </div>
          <div className="hero-carousel">
            <BackgroundSlider
              query={homepageData}
              initDelay={8} // delay before the first transition (if left at 0, the first image will be skipped
              images={["earth.png", "ev.png", "molecule.png", "arms.png"]}
              transition={0.7} // transition duration between images
              duration={8} // how long an image is shown
              styles={{}}
            >
              <div className="hero-carousel-copy-group">
                <p className="hero-carousel-headline">
                  Direct carbon sequestration
                </p>
                <p className="hero-carousel-copy">
                  With ~<span className="em">450</span> algorithmic qubits,{" "}
                  <ExplainerPopup trigger="icon">
                    Qubits — quantum bits — are the basic building blocks of
                    quantum computers. But not all qubits are equal, and{" "}
                    <em>algorithmic</em> qubits are our preferred metric for
                    describing "useful" qubits.{" "}
                    <Link to="/blog/february-23-2022-algorithmic-qubits">
                      You can read more about the metric here
                    </Link>
                    .
                  </ExplainerPopup>
                  we could help pull CO<sub>2</sub> from the air to fight
                  climate change.
                </p>
                <Link
                  to="/blog/december-09-2020-scaling-quantum-computer-roadmap"
                  className="hero-carousel-link"
                >
                  Learn more about our roadmap
                </Link>
              </div>
              <div className="hero-carousel-copy-group">
                <p className="hero-carousel-headline">
                  Better battery materials
                </p>
                <p className="hero-carousel-copy">
                  With ~<span className="em">250</span> algorithmic qubits,{" "}
                  <ExplainerPopup trigger="icon">
                    Qubits — quantum bits — are the basic building blocks of
                    quantum computers. But not all qubits are equal, and{" "}
                    <em>algorithmic</em> qubits are our preferred metric for
                    describing "useful" qubits.{" "}
                    <Link to="/blog/february-23-2022-algorithmic-qubits">
                      You can read more about the metric here
                    </Link>
                    .
                  </ExplainerPopup>
                  we could help extend the range and usefulness of electric
                  vehicles.
                </p>
                <Link
                  to="/blog/december-09-2020-scaling-quantum-computer-roadmap"
                  className="hero-carousel-link"
                >
                  Learn more about our roadmap
                </Link>
              </div>
              <div className="hero-carousel-copy-group">
                <p className="hero-carousel-headline">
                  Improved drug discovery
                </p>
                <p className="hero-carousel-copy">
                  With ~<span className="em">1,000</span> algorithmic qubits,{" "}
                  <ExplainerPopup trigger="icon">
                    Qubits — quantum bits — are the basic building blocks of
                    quantum computers. But not all qubits are equal, and{" "}
                    <em>algorithmic</em> qubits are our preferred metric for
                    describing "useful" qubits.{" "}
                    <Link to="/blog/february-23-2022-algorithmic-qubits">
                      You can read more about the metric here
                    </Link>
                    .
                  </ExplainerPopup>
                  we could help revolutionize the pharmaceutical industry.
                </p>
                <Link
                  to="/blog/december-09-2020-scaling-quantum-computer-roadmap"
                  className="hero-carousel-link"
                >
                  Learn more about our roadmap
                </Link>
              </div>
              <div className="hero-carousel-copy-group">
                <p className="hero-carousel-headline">
                  Quantum machine learning
                </p>
                <p className="hero-carousel-copy">
                  With ~<span className="em">40</span> physical qubits,{" "}
                  <ExplainerPopup trigger="icon">
                    Qubits — quantum bits — are the basic building blocks of
                    quantum computers.{" "}
                    <Link to="/technology">
                      You can learn more about how we create and control our
                      trapped ion qubits here
                    </Link>
                    .
                  </ExplainerPopup>
                  we could help begin making smarter robots, homes, cars, and
                  more.
                </p>
                <Link
                  to="/blog/december-09-2020-scaling-quantum-computer-roadmap"
                  className="hero-carousel-link"
                >
                  Learn more about our roadmap
                </Link>
              </div>
            </BackgroundSlider>
          </div>
        </div>
      </div>
      <section className="module -box customer-list">
        <div className="module-content">
          <h2 className="module-headline">Customers and Partners</h2>
          <div className="customer-grid">
            {
              homepageData.allContentfulCustomer.nodes.map(customer => (
                customer.url ? (
                  <Link to={customer.url} className="customer-link">
                    <img
                      alt={`${customer.name} Logo`}
                      src={customer.logo.url}
                    />
                  </Link>
                  ) : (
                    <div className="customer-link">
                      <img
                        alt={`${customer.name} Logo`}
                        src={customer.logo.url}
                      />
                    </div>
                  ) 
              ))
            }
          </div>
        </div>
      </section>

      <section className="module -box">
        <div className="module-content -box">
          <div className="module-copy-group -box">
            <FadeIn>
              <h2 className="module-headline">
                Meet our newest and most powerful quantum computers
              </h2>
            </FadeIn>
            <FadeIn>
              <p className="module-copy">
                Our latest systems are built for performance and practicality, enabling partners to solve their largest and most complex real-world business problems.
              </p>
              {/* box with three rows; each row has two columns; */}
              <div className="unparalled-performance-container">
                {
                  homepageData.allContentfulQuantumSystem.nodes.map(quantumSystem => (
                    <div className="unparalled-performance-row">
                      <div className="unparalled-performance-header">
                        <h4>
                          IonQ {quantumSystem.name}
                          <br/>
                        </h4>
                      </div>
                      <div className="unparalled-performance-divider">
                        <div className="unparalled-performance-column">
                          <p>
                            {quantumSystem.shortBlurb} 
                          </p>
                          <Link to={`/quantum-systems/${quantumSystem.slug}`} className="module-link">
                            Learn More
                          </Link>
                        </div>
                      </div>
                    </div>
                  ))
                }
              </div>
            </FadeIn>
          </div>
          <div className="module-illustration -tempo">
            <GatsbyImage
              image={homepageData.tempo.childImageSharp.gatsbyImageData}
              alt="image of IonQ Quantum Computer"
            />
          </div>
        </div>
      </section>
      <section className="module -trap">
        <div className="module-content -trap">
          <div className="module-copy-group -trap">
            <FadeIn>
              <p className="module-pre-headline">Trapped ion technology</p>
              <h2 className="module-headline">Powered by atoms</h2>
            </FadeIn>
            <FadeIn>
              <p className="module-copy">
                Our quantum cores use nature's qubits: atomic ions. Identical,
                perfectly isolated, and naturally quantum, ions provide
                unparalleled performance.
              </p>
              <Link to="/technology" className="module-link">
                Learn more about our technology
              </Link>
            </FadeIn>
          </div>
          <div className="module-illustration -trap">
            <GatsbyImage
              image={homepageData.trap.childImageSharp.gatsbyImageData}
              alt="image of ion trap, gold plated, with a glowing object at center"
            />
          </div>
        </div>
      </section>
      <section className="module -cloud">
        <div className="module-content -cloud">
          <div className="module-copy-group -cloud">
            <FadeIn>
              <p className="module-pre-headline">On the cloud</p>
              <h2 className="module-headline">
                A quantum cloud made for developers
              </h2>
            </FadeIn>
            <FadeIn>
              <p className="module-copy">
                Forget translating your work into yet another quantum SDK. IonQ
                works with all of the most popular cloud providers, libraries
                and tools. Just sign in and get to work.
              </p>
            </FadeIn>
            <FadeIn>
              <AnchorLink to="/quantum-cloud" className="partnership-link">
                Learn more about The IonQ Quantum Cloud
              </AnchorLink>
            </FadeIn>
            <FadeIn>
              <div className="module-partners">
                <div className="partners-item">
                  <h3 className="partners-headline">Partner Clouds</h3>
                  <p className="partners-copy">
                    Whether you’re on {partnerOrder[0]}, {partnerOrder[1]} or{" "}
                    {partnerOrder[2]}, hardware access is just a few clicks
                    away.{" "}
                  </p>
                  <CloudPartners order={partnerOrder} />
                </div>
                <div className="partners-item">
                  <h3 className="partners-headline">Direct Access</h3>
                  <p className="partners-copy">
                    IonQ partners enjoy direct access to our latest quantum
                    hardware.
                  </p>
                  <AnchorLink
                    to="/get-started#partnerships"
                    className="partnership-link"
                  >
                    Become a partner
                  </AnchorLink>
                </div>
              </div>
            </FadeIn>
          </div>
          <div className="module-illustration -cloud">
            <CloudCodePanel />
          </div>
        </div>
      </section>
      <BackgroundImage
        className="module -company"
        fluid={homepageData.companyBgDots.childImageSharp.gatsbyImageData}
        style={{
          backgroundSize: "",
          backgroundPosition: "",
        }}
      >
        <div className="module-content -company">
          <div className="company-tiles-group">
            <div className="company-tile-container">
              <div
                className="company-tile"
                data-sal="fade-in"
                data-sal-delay="0"
                data-sal-duration="150"
                data-sal-easing="ease-in-out"
              >
                <h2 className="company-tile-headline">Our mission</h2>
                <p className="company-tile-copy">
                  To build the world’s best quantum computers to solve the
                  world’s most complex problems, transforming business, society,
                  and the planet for the better.
                </p>
                <Link to="/company" className="company-tile-link">
                  Learn more about IonQ
                </Link>
              </div>
            </div>
            <div className="company-tile-container">
              <div
                className="company-tile"
                data-sal="fade-in"
                data-sal-delay="100"
                data-sal-duration="150"
                data-sal-easing="ease-in-out"
              >
                <h2 className="company-tile-headline">Our company</h2>
                <p className="company-tile-copy">
                  Building on over 25 years of research, we’ve brought quantum
                  computing out of the lab and into the real world.
                </p>
                <AnchorLink to="/company#team" className="company-tile-link">
                  Learn more about our team
                </AnchorLink>
              </div>
            </div>
            <div className="company-tile-container">
              <div
                className="company-tile"
                data-sal="fade-in"
                data-sal-delay="200"
                data-sal-duration="150"
                data-sal-easing="ease-in-out"
              >
                <h2 className="company-tile-headline">Join us</h2>
                <p className="company-tile-copy">
                  Whether you’re an expert in your field or a recent graduate,
                  if you love collaborative, challenging work, we encourage you
                  to apply.
                </p>
                <AnchorLink to="/careers" className="company-tile-link">
                  See open roles
                </AnchorLink>
              </div>
            </div>
          </div>
        </div>
      </BackgroundImage>

      <div className="module -final-cta">
        <div className="module-content -final-cta">
          <div className="module-copy-group -final-cta">
            <FadeIn>
              <h2 className="module-headline -final-cta">
                Harness the world-changing power of quantum computing
              </h2>
              <p className="module-copy">
                Get started on one of our partner clouds in as little as ten
                minutes, or contact us to discuss a custom partnership for your
                business.
              </p>
            </FadeIn>
            <div
              className="module-double-cta"
              data-sal="fade-in"
              data-sal-delay="0"
              data-sal-duration="150"
              data-sal-easing="ease-in-out"
            >
              <Link to="/get-started" className="double-cta-primary">
                Start your quantum journey
              </Link>
              <AnchorLink
                to="/get-started#work-with-us"
                className="double-cta-secondary"
              >
                Contact sales
              </AnchorLink>
            </div>
          </div>
          <div className="module-illustration -final-cta">
            <img aria-hidden alt="" role="presentation" src={connectivity} />
          </div>
        </div>
      </div>
    </Layout>
  )
}

export default IndexPage
